import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  HStack,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Badge,
  Button,
  Divider,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiClock } from 'react-icons/fi';
import { updateTemplate, transformTemplateToCamelCase } from '../../../api/supabase/automations';
import { getTriggerDescription } from '../../../components/CRM/Campaigns/campaignConstants';

export default function CampaignSettingsDrawer({ isOpen, onClose, campaign, canEdit, isAdmin, onUpdated, onShowHistory }) {
  const template = campaign?.template || {};
  const enrollmentRules = template.enrollmentRules || {};
  const [allowReEnrollment, setAllowReEnrollment] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    setAllowReEnrollment(enrollmentRules.allow_re_enrollment === true);
    setIsVisible(template.isVisible !== false);
  }, [campaign?.id, enrollmentRules.allow_re_enrollment, template.isVisible]);

  const handleToggleReEnrollment = async (checked) => {
    setAllowReEnrollment(checked);
    setSaving(true);
    try {
      const newRules = { ...enrollmentRules, allow_re_enrollment: checked };
      const updated = await updateTemplate(template.id, { enrollmentRules: newRules });
      onUpdated?.(transformTemplateToCamelCase(updated));
      toast({
        title: checked ? 'Re-enrollment enabled' : 'Re-enrollment disabled',
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      setAllowReEnrollment(!checked);
      toast({ title: 'Failed to save', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (checked) => {
    setIsVisible(checked);
    setSaving(true);
    try {
      const updated = await updateTemplate(template.id, { isVisible: checked });
      onUpdated?.(transformTemplateToCamelCase(updated));
      toast({
        title: checked ? 'Campaign visible to all users' : 'Campaign hidden from users',
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      setIsVisible(!checked);
      toast({ title: 'Failed to save', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  if (!campaign) return null;

  const triggerDesc = getTriggerDescription(
    template.triggerType,
    template.triggerConfig
  );

  const isCompanyTemplate = template.ownershipType !== 'user';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" pb={3}>
          <Text fontSize="lg" fontWeight="600">{template.name || 'Campaign Settings'}</Text>
          <HStack spacing={2} mt={1}>
            <Text fontSize="xs" color={labelColor} fontWeight="normal">
              {triggerDesc}
            </Text>
            <Badge colorScheme={isCompanyTemplate ? 'blue' : 'gray'} fontSize="2xs" variant="subtle">
              {isCompanyTemplate ? 'Company' : 'Personal'}
            </Badge>
          </HStack>
        </DrawerHeader>
        <DrawerBody py={6}>
          <VStack align="stretch" spacing={6}>
            {/* Trigger info */}
            <VStack align="stretch" spacing={1}>
              <Text fontSize="xs" fontWeight="600" textTransform="uppercase" color={labelColor}>
                Trigger
              </Text>
              <HStack>
                <Badge colorScheme="purple" fontSize="xs">
                  {(template.triggerType || '').replace(/_/g, ' ')}
                </Badge>
                <Badge colorScheme={template.isActive ? 'green' : 'gray'} fontSize="xs">
                  {template.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </HStack>
            </VStack>

            <Divider />

            {/* Enrollment Settings */}
            <VStack align="stretch" spacing={4}>
              <Text fontSize="xs" fontWeight="600" textTransform="uppercase" color={labelColor}>
                Enrollment Settings
              </Text>

              <FormControl display="flex" alignItems="start" justifyContent="space-between">
                <VStack align="start" spacing={0} flex="1" mr={4}>
                  <FormLabel mb={0} fontSize="sm" fontWeight="500">
                    Allow Re-enrollment
                  </FormLabel>
                  <Text fontSize="xs" color={labelColor}>
                    When enabled, this campaign can trigger multiple times for the same contact.
                    Useful for automation-style workflows (e.g., every time a tag is added).
                  </Text>
                  {!canEdit && (
                    <Text fontSize="xs" color="orange.500" mt={1}>
                      Only the campaign owner or an admin can change this.
                    </Text>
                  )}
                </VStack>
                <Switch
                  colorScheme="blue"
                  isChecked={allowReEnrollment}
                  isDisabled={!canEdit || saving}
                  onChange={(e) => handleToggleReEnrollment(e.target.checked)}
                  mt={1}
                />
              </FormControl>
            </VStack>

            {/* Visibility — admin only, company campaigns only */}
            {isAdmin && isCompanyTemplate && (
              <>
                <Divider />
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="xs" fontWeight="600" textTransform="uppercase" color={labelColor}>
                    Visibility
                  </Text>
                  <FormControl display="flex" alignItems="start" justifyContent="space-between">
                    <VStack align="start" spacing={0} flex="1" mr={4}>
                      <FormLabel mb={0} fontSize="sm" fontWeight="500">
                        Visible to Users
                      </FormLabel>
                      <Text fontSize="xs" color={labelColor}>
                        When enabled, this campaign appears in &quot;Available Campaigns&quot; for all
                        Origin CRM users. When disabled, only admins can see and subscribe to it.
                      </Text>
                    </VStack>
                    <Switch
                      colorScheme="green"
                      isChecked={isVisible}
                      isDisabled={saving}
                      onChange={(e) => handleToggleVisibility(e.target.checked)}
                      mt={1}
                    />
                  </FormControl>
                </VStack>
              </>
            )}

            <Divider />

            {/* Campaign info */}
            <VStack align="stretch" spacing={2}>
              <Text fontSize="xs" fontWeight="600" textTransform="uppercase" color={labelColor}>
                Details
              </Text>
              {template.description && (
                <Text fontSize="sm" color={labelColor}>{template.description}</Text>
              )}
              <HStack justify="space-between">
                <Text fontSize="xs" color={labelColor}>Version</Text>
                <Text fontSize="xs" fontWeight="500">v{template.version || 1}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="xs" color={labelColor}>Status</Text>
                <Badge
                  colorScheme={campaign.isEnabled ? 'green' : 'gray'}
                  fontSize="xs"
                >
                  {campaign.isEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="xs" color={labelColor}>Ownership</Text>
                <Badge
                  colorScheme={isCompanyTemplate ? 'blue' : 'gray'}
                  fontSize="xs"
                  variant="subtle"
                >
                  {isCompanyTemplate ? 'Company' : 'Personal'}
                </Badge>
              </HStack>
            </VStack>

            {/* Version history — owners/admins can browse snapshots and roll back */}
            {canEdit && onShowHistory && (
              <>
                <Divider />
                <Button
                  leftIcon={<FiClock />}
                  size="sm"
                  variant="outline"
                  alignSelf="flex-start"
                  onClick={() => onShowHistory(template)}
                >
                  Version History
                </Button>
              </>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
